import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CardHeader } from 'reactstrap';
import { When } from 'react-if';
import './styles.css';
import { Categories, DefaultCategory, SidekickCategories } from '../../../../static/categories';
import { format } from 'date-fns';
import { ReactComponent as NewIcon } from './new-icon.svg';
import { POST_PAGE_TYPES } from '../../../../constants/postPageTypes';
import { useInViewEffect } from 'react-hook-inview';
import { IS_VALIDATE_APP } from '../../../../constants/projects';

const PostHeader = ({
    categoryId,
    firstName,
    lastName,
    createdTimestamp,
    totalDonations,
    reviewsCount,
    unreadPostIds,
    invitationsPostIds,
    postId,
    currentPostPage,
    viewInvitationPost,
}) => {
    const createdTime = useMemo(() => {
        return format(createdTimestamp.toDate(), 'DD/MM/YYYY');
    }, [createdTimestamp]);

    const hasInvitationsPostIdsNotification = useMemo(() => {
        return currentPostPage === POST_PAGE_TYPES.INVITATIONS
            ? (invitationsPostIds || []).includes(postId)
            : false;
    }, [currentPostPage, invitationsPostIds, postId]);

    const hasUnreadPostIdsNotification = useMemo(() => {
        return currentPostPage === POST_PAGE_TYPES.MY_POSTS
            ? (unreadPostIds || []).includes(postId)
            : false;
    }, [unreadPostIds, postId, currentPostPage]);

    const ref = useInViewEffect(
        ([entry]) => {
            if (
                entry.isIntersecting &&
                hasInvitationsPostIdsNotification &&
                currentPostPage === POST_PAGE_TYPES.INVITATIONS
            ) {
                viewInvitationPost(postId);
            }
        },
        { threshold: 0.5 }
    );

    const userName = useMemo(() => {
        return lastName.length ? `${firstName} ${lastName[0]} .` : firstName;
    }, [firstName, lastName]);

    const category = useMemo(() => {
        const category = IS_VALIDATE_APP
            ? Categories[categoryId]
                ? Categories[categoryId]
                : DefaultCategory
            : SidekickCategories[categoryId]
            ? SidekickCategories[categoryId]
            : DefaultCategory;

        return {
            label: category.label,
            color: category.color,
        };
    }, [categoryId]);

    return (
        <>
            <When condition={hasInvitationsPostIdsNotification || hasUnreadPostIdsNotification}>
                {() => (
                    <div className="header__new-label d-flex justify-content-center align-items-center">
                        <NewIcon />
                        <span className="ml-1">New</span>
                    </div>
                )}
            </When>
            <CardHeader className="d-flex justify-content-between align-items-center p-0 header">
                <div
                    ref={ref}
                    className="d-flex justify-content-between align-items-center header__info w-100"
                >
                    <div style={{ backgroundColor: category.color }}>
                        <h6 className="header header__category-label header__text">
                            {category.label}
                        </h6>
                    </div>
                    <div className="d-flex justify-content-center align-items-center">
                        <h6 className="mr-5 header header__text">{reviewsCount}&nbsp;Reviews</h6>
                        <h6 className="mr-5 header header__text">{createdTime}</h6>
                        <When condition={!!userName}>
                            <h6 className="mr-5 header header__text">{userName}</h6>
                        </When>
                        <h6 className="mr-5 header header__text header__donation">
                            &#163;{totalDonations} Donated
                        </h6>
                    </div>
                </div>
            </CardHeader>
        </>
    );
};

PostHeader.propTypes = {
    categoryId: PropTypes.string.isRequired,
    createdTimestamp: PropTypes.object.isRequired,
    firstName: PropTypes.string.isRequired,
    totalDonations: PropTypes.number.isRequired,
    lastName: PropTypes.string,
    reviewsCount: PropTypes.number,
    postId: PropTypes.string.isRequired,
    unreadPostIds: PropTypes.array.isRequired,
    invitationsPostIds: PropTypes.array.isRequired,
    currentPostPage: PropTypes.number.isRequired,
    viewInvitationPost: PropTypes.func.isRequired,
};

export default React.memo(PostHeader);
